import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import * as Spinners from '@Services/Spinners';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import ShowMoreComponent from '@Components/Results/utils/show-more.component';
import SubstanceQuantitativeTable from '@Components/Results/substances/tables/quantitative/substance-quantitative-table.component';
import TableColumnsSwitcherComponent from '@Components/Results/reactions/reaction-details/table-columns-switcher/table-columns-switcher.component';
import SubstanceQuantitativeReport from '@Components/Results/substances/tables/quantitative/substance-quantitative-report.component';
import SubstanceData from '@Services/SubstanceData';
import { HEATMAP_CELL_DETAILS_DIALOG_CONTENT_CLASS_NAME } from '@Pages/Heatmap/HeatmapCellDetailsDialog/constants';

import './substance-quantitative-data.custom.scss';

function SubstanceQuantitativeData(props) {
  const {
    data: initialData,
    uniqueId,
    metadata,
    total,
    showTitle,
    initialItemsAmount,
    columnsViewedByDefault,
    retrieveData,
    track,
  } = props;

  const isReport = initialData.layout === 'list';
  const id = `${uniqueId}-quantitative`;
  const containerRef = useRef(null);
  const columnSwitcherRef = useRef(null);
  const cacheRef = useRef(null);

  const [data, setData] = useState(() => {
    if (initialData.headers && initialData.rows) {
      return processData(initialData);
    }
    return null;
  });
  const [headersReady, setHeadersReady] = useState(isReport);
  const [error, setError] = useState(!(initialData.headers && initialData.rows));

  function processData(data) {
    if (isReport) {
      return {
        headers: data.headers,
        rows: SubstanceData.buildReportData(data.headers, data.rows),
      };
    }
    return SubstanceData.alterDataShownQuan(data);
  }

  const updateHeaders = useCallback(
    (headers) => {
      if (!headersReady) Spinners.terminate(id);
      setData((prevData) => ({
        ...prevData,
        headers,
      }));
      setHeadersReady(true);
    },
    [headersReady, id]
  );

  const showAllNonEmptyCols = useCallback(() => {
    if (columnSwitcherRef.current) {
      columnSwitcherRef.current.showAllNonEmptyCols();
    }
  }, []);

  const showMore = useCallback(
    (firstItem = 1, lastItem = initialItemsAmount) => {
      return new Promise((resolve, reject) => {
        if (cacheRef.current) {
          appendRows(cacheRef.current);
          cacheRef.current = null;
          resolve();
          return;
        }
        Spinners.activate(id);
        retrieveData(firstItem, lastItem)
          .then(({ rows }) => {
            appendRows(rows);
            if (track) track(rows, 'showMoreQuan', null, firstItem, lastItem);
            return rows;
          })
          .catch(() => reject(new Error('No quantitative data found')))
          .finally(() => {
            Spinners.terminate(id);
            resolve();
          });
      });
    },
    [id, initialItemsAmount, retrieveData, track]
  );

  function appendRows(rows) {
    setData((prevData) => {
      const newData = { ...prevData, rows: prevData.rows?.concat(rows) };
      return processData(newData);
    });
  }

  const showLess = useCallback(() => {
    setData((prevData) => {
      const newData = { ...prevData };
      cacheRef.current = newData.rows?.splice(initialItemsAmount);
      newData.rows = [...newData.rows];
      return newData;
    });
  }, [initialItemsAmount]);

  useEffect(() => {
    if (!isReport && initialData.headers && initialData.rows) {
      Spinners.activate(id);
    }
    if (!(initialData.headers && initialData.rows)) {
      setError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollContainerSelector = metadata.heatmapMode
    ? `.${HEATMAP_CELL_DETAILS_DIALOG_CONTENT_CLASS_NAME}`
    : null;

  if (error) {
    return <div className="substance-results-quantitative-error">Information currently unavailable.</div>;
  }

  return (
    <div className="substance-results-quantitative" id={id} ref={containerRef}>
      {showTitle && (
        <div className="typography-small">
          <strong>Quantitative Results</strong>
        </div>
      )}

      {!isReport && (
        <>
          <div className="rx-btn-group--right">
            <TableColumnsSwitcherComponent
              headers={data && data.headers}
              uniqueId={id}
              containerRef={containerRef}
              ref={columnSwitcherRef}
              updateHeaders={updateHeaders}
              defaultColumns={columnsViewedByDefault}
              scrollable={metadata.heatmapMode}
            />
          </div>
          {headersReady && (
            <SubstanceQuantitativeTable
              metadata={metadata}
              data={data}
              uniqueId={id}
              showAllNonEmptyCols={showAllNonEmptyCols}
            />
          )}
        </>
      )}
      {isReport && <SubstanceQuantitativeReport metadata={metadata} data={data.rows} total={total} />}

      <RxSpinnerComponent spinnerId={id} position="relative" />

      <div className="rx-btn-group--center">
        <ShowMoreComponent
          initial={initialItemsAmount}
          scrollContainerSelector={scrollContainerSelector}
          metadata={metadata}
          current={data && data.rows && data.rows.length}
          total={total}
          container={`#${id}`}
          moreHandler={({ firstItem, lastItem }) => showMore(firstItem, lastItem)}
          lessHandler={showLess}
        />
      </div>
    </div>
  );
}

SubstanceQuantitativeData.defaultProps = {
  initialItemsAmount: 7,
};

SubstanceQuantitativeData.propTypes = {
  metadata: MetadataPropTypesShape,
  total: PropTypes.number.isRequired,
  data: PropTypes.shape({
    headers: PropTypes.array,
    rows: PropTypes.array,
    layout: PropTypes.string,
  }).isRequired,
  uniqueId: PropTypes.string.isRequired,
  retrieveData: PropTypes.func.isRequired,
  showTitle: PropTypes.bool,
  initialItemsAmount: PropTypes.number,
  columnsViewedByDefault: PropTypes.array,
  substanceId: PropTypes.string.isRequired,
  track: PropTypes.func,
};

export default SubstanceQuantitativeData;
